if (!customElements.get("m-product-tabs")) {
  const controlUpdates = new Map();
  let controlFrame = 0;
  function queueControls(owner, panel, reveal = false) {
    const previous = controlUpdates.get(panel);
    controlUpdates.set(panel, { owner, reveal: reveal || Boolean(previous && previous.reveal) });
    if (controlFrame) return;
    controlFrame = requestAnimationFrame(() => {
      controlFrame = 0;
      const measurements = [];
      for (const [panel, { owner, reveal }] of controlUpdates) {
        if (!owner.isConnected || (owner.deferInactiveSliders && !panel.classList.contains("active"))) continue;
        const image = panel.querySelector(".m-image") || panel.querySelector(".m-placeholder-svg");
        const controls = panel.querySelector(".m-slider-controls");
        if (image && controls) measurements.push({ controls, height: image.clientHeight, reveal });
      }
      controlUpdates.clear();
      for (const { controls, height, reveal } of measurements) {
        const offset = parseInt(height) / 2 + "px";
        if (controls.style.getPropertyValue("--offset-top") !== offset) controls.style.setProperty("--offset-top", offset);
        if (reveal) {
          controls.querySelector(".m-slider-controls__button-prev")?.classList.remove("m:hidden");
          controls.querySelector(".m-slider-controls__button-next")?.classList.remove("m:hidden");
        }
      }
    });
  }
  class MProductTabs extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        loadMoreBtn: "[data-load-more-product]",
        productsContainer: "[data-products-container]",
        tabContent: [".m-tab-content"],
      };
      this.tabSliders = [];
      this.deferInactiveSliders = this.id.endsWith("__162251092958fcda7c") && document.body.classList.contains("template-index");
      this.visitedPanels = new WeakSet();
      this.sliderTimers = new Map();
      this.controlTimers = new Map();
      this.navigationBindings = new WeakMap();
      this.loadMoreBindings = new WeakSet();
      this.onBreakpoint = this.handleBreakpoint.bind(this);
      this.onBlockSelect = (event) => {
        if (this.contains(event.target) && event.target.classList.contains("m-tab-content")) this.tabs.setActiveTab(Number(event.target.dataset.index));
      };
      this.sliderEnabled = this.dataset.enableSlider === "true";
      this.mobileSliderDisable = this.dataset.mobileDisableSlider === "true";
      this.buttonType = this.dataset.buttonType;
      this.domNodes = queryDomNodes(this.selectors, this);
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = this.dataset.items;
    }

    connectedCallback() {
      if (!this.initialized) {
        this.initialized = true;
        this.init();
      } else {
        this.handleBreakpoint();
      }
      document.addEventListener("matchMobile", this.onBreakpoint);
      document.addEventListener("unmatchMobile", this.onBreakpoint);
      if (Shopify.designMode) document.addEventListener("shopify:block:select", this.onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener("matchMobile", this.onBreakpoint);
      document.removeEventListener("unmatchMobile", this.onBreakpoint);
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
      for (const timer of this.sliderTimers.values()) clearTimeout(timer);
      for (const timer of this.controlTimers.values()) clearTimeout(timer);
      this.sliderTimers.clear();
      this.controlTimers.clear();
      for (const panel of this.domNodes.tabContent) controlUpdates.delete(panel);
    }

    handleBreakpoint() {
      if (this.sliderEnabled) for (const panel of this.domNodes.tabContent) this.initSliderByScreenSize(panel);
      if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
        for (const panel of this.domNodes.tabContent) this.initLoadMore(panel);
      }
    }

    init() {
      if (this.sliderEnabled) {
        for (let block of this.domNodes.tabContent) {
          this.initSliderByScreenSize(block);

        }
      }
      if (!this.sliderEnabled && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;

        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;
        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      this.initTabs();
      this.initMobileSelect();
    }

    initTabs() {
      this.tabs = new MinimogTheme.Tabs(this);
      const setActiveTab = this.tabs.setActiveTab.bind(this.tabs);
      this.tabs.setActiveTab = (index) => {
        setActiveTab(Number(index));
        const panel = this.tabs.currentTab;
        if (!panel || !this.sliderEnabled) return;
        this.visitedPanels.add(panel);
        if (!(MinimogTheme.config.mqlMobile && this.mobileSliderDisable)) {
          const wrapper = panel.querySelector(".m-mixed-layout__wrapper");
          if (wrapper?.swiper && !wrapper.swiper.destroyed) wrapper.swiper.update();
          else this.initSlider(panel);
        }
        queueControls(this, panel, true);
      };
    }

    initSliderByScreenSize(sliderContainer) {
      if (this.deferInactiveSliders && sliderContainer !== this.domNodes.tabContent[0] && !sliderContainer.classList.contains("active") && !this.visitedPanels.has(sliderContainer)) return;
      const mobileDisableSlider = this.mobileSliderDisable;
      const slider = sliderContainer.querySelector(".m-mixed-layout__wrapper");
      const controlsContainer = sliderContainer.querySelector(".m-slider-controls");

      if (MinimogTheme.config.mqlMobile && mobileDisableSlider) {
        controlsContainer && controlsContainer.classList.add("m:hidden");
        slider.classList.remove("swiper-container");
        if (this.sliderTimers.has(sliderContainer)) {
          clearTimeout(this.sliderTimers.get(sliderContainer));
          this.sliderTimers.delete(sliderContainer);
        }
        if (slider.swiper && !slider.swiper.destroyed) slider.swiper.destroy(false, true);
      } else {
        controlsContainer && controlsContainer.classList.remove("m:hidden");
        if (slider.swiper && !slider.swiper.destroyed) {
          queueControls(this, sliderContainer);
          return;
        }
        if (this.sliderTimers.has(sliderContainer)) return;
        this.sliderTimers.set(sliderContainer, setTimeout(() => {
          this.sliderTimers.delete(sliderContainer);
          if (this.isConnected) this.initSlider(sliderContainer);
        }));
      }
    }

    initSlider(sliderContainer) {
      const layoutWrapper = sliderContainer.querySelector(".m-product-list");
      const swiper = sliderContainer && sliderContainer.querySelector(".m-mixed-layout__wrapper");
      if (!swiper || (swiper.swiper && !swiper.swiper.destroyed)) return;
      if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable) return;
      const controlsContainer = sliderContainer.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      const slideItemsLength =
        sliderContainer &&
        sliderContainer.querySelector(".swiper-wrapper") &&
        sliderContainer.querySelector(".swiper-wrapper").childElementCount;
      if (parseInt(this.items) >= slideItemsLength) {
        controlsContainer && controlsContainer.classList.add("m:hidden");
        layoutWrapper.classList.add("m-mixed-layout--mobile-grid");
        return;
      }

      swiper && swiper.classList.add("swiper-container");

      let slider = new MinimogLibs.Swiper(swiper, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: this.enableFlashsale ? false : true,
        pagination: this.showPagination
          ? {
              el: sliderContainer.querySelector(".swiper-pagination"),
              clickable: true,
            }
          : false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.items) >= 3 ? 3 : parseInt(this.items),
          },
          992: {
            slidesPerView: parseInt(this.items) >= 4 ? 4 : parseInt(this.items),
          },
          1280: {
            slidesPerView: parseInt(this.items),
          },
        },
        threshold: 2,
        on: {
          init: () => {
            clearTimeout(this.controlTimers.get(sliderContainer));
            this.controlTimers.set(sliderContainer, setTimeout(() => {
              this.controlTimers.delete(sliderContainer);
              queueControls(this, sliderContainer, true);
            }, 200));
          },
          breakpoint: (swiper, breakpointParams) => {
            if (controlsContainer) {
              const { slidesPerView } = breakpointParams;
              if (slideItemsLength > slidesPerView) {
                controlsContainer.classList.remove("m:hidden");
                swiper.allowTouchMove = true;
              } else {
                controlsContainer.classList.add("m:hidden");
                swiper.allowTouchMove = false;
              }
            }
          },
        },
      });

      if (slider && this.showNavigation) {
        const previous = this.navigationBindings.get(sliderContainer);
        if (previous) {
          previous.prevButton?.removeEventListener("click", previous.prev);
          previous.nextButton?.removeEventListener("click", previous.next);
        }
        const prev = () => slider.slidePrev();
        const next = () => slider.slideNext();
        prevButton?.addEventListener("click", prev);
        nextButton?.addEventListener("click", next);
        this.navigationBindings.set(sliderContainer, { prevButton, nextButton, prev, next });
      }
    }

    initMobileSelect() {
      this.select = this.querySelector("[data-tab-select]");
      this.select?.addEventListener("change", () => this.tabs.setActiveTab(Number(this.select.value)));
    }

    initLoadMore(wrapper) {
      if (this.loadMoreBindings.has(wrapper)) return;
      this.loadMoreBindings.add(wrapper);
      addEventDelegate({
        context: wrapper,
        selector: this.selectors.loadMoreBtn,
        handler: (e) => {
          e.preventDefault();
          this.handleLoadMore(wrapper);
        },
      });
    }

    handleLoadMore(wrapper) {
      const loadBtn = wrapper.querySelector(this.selectors.loadMoreBtn);
      const productsContainer = wrapper.querySelector(this.selectors.productsContainer);

      let currentPage = wrapper.dataset.page;
      currentPage = parseInt(currentPage);
      const totalPages = wrapper.dataset.totalPages;
      this.toggleLoading(loadBtn, true);

      const url = wrapper.dataset.url;
      const dataUrl = `${url}?page=${currentPage + 1}&section_id=${this.id}`;
      fetchCache(dataUrl).then((html) => {
        currentPage++;
        wrapper.dataset.page = currentPage;
        this.toggleLoading(loadBtn, false);
        const dom = generateDomFromString(html);
        const tabId = wrapper.getAttribute("id");
        const products = dom.querySelector(`#${tabId} ${this.selectors.productsContainer}`);

        if (products) {
          Array.from(products.childNodes).forEach((product) => productsContainer.appendChild(product));
        }

        if (currentPage >= parseInt(totalPages)) loadBtn && loadBtn.remove();
      });
      loadBtn.blur();
    }

    toggleLoading(loadBtn, status) {
      if (!loadBtn) return;
      if (status) {
        loadBtn.classList.add("m-spinner-loading");
      } else {
        loadBtn.classList.remove("m-spinner-loading");
      }
    }
  }
  customElements.define("m-product-tabs", MProductTabs);
}

